import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BackButtonService } from '../../back-button.service';
import { CodaltComponent } from '../../codalt.component';
import { CustomerProject } from '../../customer-project.interface';
import { DateNLFormatAdapter } from '../../date-adapters/date-nlformat-adapter';
import { NewsDocument } from '../../models/news-document.interface';
import { NewsImage } from '../../models/news-image.interface';
import { News } from '../../models/news.interface';
import { Routenames } from '../../route-names.enum';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { NewsService } from '../../services/news.service';
import { PageTitleService } from '../../services/page-title/page-title.service';
import { Utils } from '../../utils.class';
import { handleTimeValueChange } from '../../utils/handle-time-value-change';
import { validTime } from '../../validators/valid-time.validator';

@Component({
    selector: 'app-news-edit',
    templateUrl: './news-edit.component.html',
    styleUrls: ['../../shared-editor-date-columns.scss'],
    providers: [{ provide: DateAdapter, useClass: DateNLFormatAdapter }],
    standalone: false,
})
export class NewsEditComponent extends CodaltComponent implements OnInit {
    reserve = false;
    saving = false;
    form: FormGroup;
    newsItem: News;
    projects: CustomerProject[];

    constructor(
        private newsService: NewsService,
        private customerProjectService: CustomerProjectService,
        private router: Router,
        private backButtonService: BackButtonService,
        private dialog: ConfirmDialogService,
        private route: ActivatedRoute,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    reorderImages(event: CdkDragDrop<NewsImage[]>) {
        const movedImage = this.newsItem.images[event.previousIndex];
        this.newsItem.images.splice(event.previousIndex, 1);
        this.newsItem.images.splice(event.currentIndex, 0, movedImage);
        this.subscriptions.add(this.newsService.sortImages(this.newsItem).subscribe());
    }

    imageAdaptChanged() {
        this.subscriptions.add(this.newsService.sortImages(this.newsItem).subscribe());
    }

    ngOnInit(): void {
        this.pageTitleService.setTitle(this.getPageTitleLabel());
        this.watchRouteParams(this.RouteParamnames.newsId, ({ newsId }) => this.getNews(newsId));
        this.subscriptions.add(
            this.customerProjectService.getProjects(true).subscribe(projects => (this.projects = projects))
        );
    }

    public openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.form.dirty) {
                this.dialog
                    .confirm(
                        'Niet opgeslagen wijzigingen',
                        `Wilt u de niet opgeslagen wijzigingen opslaan of verwerpen?`,
                        'Wijzigingen opslaan',
                        'Wijzigingen verwerpen'
                    )
                    .then(() => {
                        this.save();
                        resolve(false);
                    })
                    .catch(() => resolve(true));
            } else {
                resolve(true);
            }
        });
    }

    save() {
        Utils.triggerValidation(this.form);
        if (this.form.valid) {
            this.saving = true;
            this.newsService.save(this.form.value).subscribe(
                news => {
                    Object.assign(this.newsItem, news.data);
                    this.form.markAsPristine();
                    this.saving = false;
                    if (new Date(this.form.value['publish_date']).getTime() > Date.now()) {
                        // Future updates belong in the future overview. Go back when that overview is
                        // already the previous entry, otherwise replacing this route would duplicate it.
                        const futureUrl = `/${this.Routenames.news}/${this.Routenames.future}`;
                        const previousUrl = this.router.lastSuccessfulNavigation?.previousNavigation?.finalUrl
                            ?.toString()
                            .split('?')[0];
                        if (previousUrl === futureUrl) {
                            this.backButtonService.navigateBack();
                        } else {
                            this.router.navigateByUrl(futureUrl, { replaceUrl: true });
                        }
                    } else {
                        this.backButtonService.navigateBack();
                    }
                },
                () => (this.saving = false)
            );
        }
    }

    uploadedFiles(files) {
        if (!this.newsItem.images) {
            this.newsItem.images = [];
        }
        files.file.forEach(file =>
            this.subscriptions.add(
                this.newsService.addImage(this.newsItem.id, file.filename).subscribe(img => {
                    const loading = this.newsItem.images.find(p => !p.id);
                    if (loading) {
                        img.data.thumb = loading.thumb;
                        img.data.adapt_image = true;
                        Object.assign(loading, img.data);
                    } else {
                        this.newsItem.images.push(img.data);
                    }
                })
            )
        );
    }

    uploadFiles(files?: File[]) {
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (!this.newsItem.images) {
                    this.newsItem.images = [];
                }
                this.newsItem.images.push({
                    thumb: reader.result.toString(),
                } as NewsImage);
            };
        });
    }

    deleteImage(image: NewsImage) {
        this.subscriptions.add(
            this.newsService.rmImage(image.id).subscribe(() => {
                const index = this.newsItem.images.indexOf(image);
                this.newsItem.images.splice(index, 1);
            })
        );
    }

    uploadedMainImage(file) {
        this.form.get('image').setValue(file ? file.file.filename : null);
    }

    uploadMainImage(files) {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
            this.newsItem.image_thumb = reader.result.toString();
        };
        this.form.get('image').setValue(null);
    }

    reorderDocuments(event: CdkDragDrop<NewsDocument[]>) {
        const movedImage = this.newsItem.documents[event.previousIndex];
        this.newsItem.documents.splice(event.previousIndex, 1);
        this.newsItem.documents.splice(event.currentIndex, 0, movedImage);
        this.subscriptions.add(this.newsService.sortDocuments(this.newsItem).subscribe());
    }

    deleteDocument(document: NewsDocument) {
        this.subscriptions.add(
            this.newsService.rmDocument(document.id).subscribe(() => {
                const index = this.newsItem.documents.indexOf(document);
                this.newsItem.documents.splice(index, 1);
            })
        );
    }

    uploadedDocuments(files) {
        if (!this.newsItem.documents) {
            this.newsItem.documents = [];
        }
        files.file.forEach(file =>
            this.subscriptions.add(
                this.newsService.addDocument(this.newsItem.id, file.filename).subscribe(img => {
                    const loading = this.newsItem.documents.find(p => !p.id);
                    if (loading) {
                        Object.assign(loading, img.data);
                    } else {
                        this.newsItem.documents.push(img.data);
                    }
                })
            )
        );
    }

    private getNews(slug) {
        this.reserve = slug === Routenames.newItem;
        const news$ = this.reserve
            ? this.newsService.reserve()
            : this.newsService.get(slug, this.route.snapshot.routeConfig?.path === this.Routenames.copy);
        this.subscriptions.add(
            news$.subscribe(
                news => {
                    this.newsItem = news.data;
                    this.pageTitleService.setTitle(this.getPageTitleLabel(), this.newsItem.title);
                    this.generateForm(this.newsItem);
                },
                error =>
                    this.dialog
                        .confirm('Fout', error.message, 'Terug naar overzicht')
                        .then(() => this.backButtonService.navigateBack())
            )
        );
    }

    private generateForm(newsItem: News) {
        const images = newsItem.images ? newsItem.images.map(img => img.id) : [];
        if (!newsItem.publish_date) {
            newsItem.publish_date = new Date();
            if (newsItem.publish_date.getMinutes() > 44) {
                newsItem.publish_date.setHours(newsItem.publish_date.getHours() + 2);
            } else {
                newsItem.publish_date.setHours(newsItem.publish_date.getHours() + 1);
            }
            newsItem.publish_date.setMinutes(0);
        }
        const fcPublishTime = new FormControl(
            newsItem.publish_date ? formatDate(newsItem.publish_date, 'HH:mm', 'nl') : null,
            { validators: [validTime(), Validators.required], updateOn: 'blur' }
        );
        const fcPublishDate = new FormControl(newsItem.publish_date ? newsItem.publish_date : new Date());
        this.form = new FormGroup({
            id: new FormControl(newsItem.id),
            title: new FormControl(newsItem.title, Validators.required),
            alert: new FormControl(newsItem.alert || false),
            adapt_image: new FormControl(newsItem.adapt_image ?? true),
            content: new FormControl(newsItem.content, Validators.required),
            publish_date: fcPublishDate,
            publish_time: fcPublishTime,
            images: new FormControl(images),
            image: new FormControl(newsItem.image),
            customer_project_id: new FormControl(newsItem.customer_project_id, Validators.required),
        });
        this.subscriptions.add(
            fcPublishTime.valueChanges.subscribe(value => handleTimeValueChange(value, fcPublishTime))
        );
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                if (fcPublishTime.value && fcPublishDate.value) {
                    const time = fcPublishTime.value.split(':');
                    if (time.length === 2) {
                        const date = new Date(fcPublishDate.value);
                        date.setHours(+time[0]);
                        date.setMinutes(+time[1]);
                        if (date.toString() !== new Date(fcPublishDate.value).toString()) {
                            fcPublishDate.setValue(date);
                        }
                    }
                }
            })
        );
    }

    private getPageTitleLabel(): string {
        return this.route.snapshot.routeConfig?.path === this.Routenames.copy ? 'Update kopiëren' : 'Update bewerken';
    }
}
